package dev.wyns.ventrova.ui.android

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Surface
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.wyns.ventrova.app.AppUiState
import dev.wyns.ventrova.app.AppViewModel
import dev.wyns.ventrova.app.RadiusOptions
import dev.wyns.ventrova.app.StrictnessStop
import dev.wyns.ventrova.data.model.PlaceCategory
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.filter_clear_all
import dev.wyns.ventrova.resources.filter_distance
import dev.wyns.ventrova.resources.filter_how_plant_based
import dev.wyns.ventrova.resources.filter_kind_of_place
import dev.wyns.ventrova.resources.filter_open_now
import dev.wyns.ventrova.resources.filter_open_now_description
import dev.wyns.ventrova.resources.filter_show_places
import dev.wyns.ventrova.resources.filters
import dev.wyns.ventrova.resources.ic_check
import dev.wyns.ventrova.ui.components.LevelGlyph
import dev.wyns.ventrova.ui.components.VIcon
import dev.wyns.ventrova.ui.components.bodyStyle
import dev.wyns.ventrova.ui.components.glyphLevel
import dev.wyns.ventrova.ui.components.radiusLabel
import dev.wyns.ventrova.ui.components.stopDescription
import dev.wyns.ventrova.ui.components.stopLabel
import dev.wyns.ventrova.ui.theme.VentrovaColors
import dev.wyns.ventrova.ui.theme.icon
import dev.wyns.ventrova.ui.theme.label
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
internal fun AndroidFilterSheet(state: AppUiState, viewModel: AppViewModel, onDismiss: () -> Unit) {
    val filters = state.filters
    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true),
        containerColor = VentrovaColors.Canvas,
        shape = RoundedCornerShape(topStart = 28.dp, topEnd = 28.dp),
    ) {
        Column(Modifier.verticalScroll(rememberScrollState()).navigationBarsPadding().padding(start = 24.dp, end = 24.dp, bottom = 20.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(stringResource(Res.string.filters), style = bodyStyle(22.sp), modifier = Modifier.weight(1f))
                Text(
                    stringResource(Res.string.filter_clear_all),
                    style = bodyStyle(14.sp, FontWeight.SemiBold, VentrovaColors.AccentDark),
                    modifier = Modifier.clickable(onClick = viewModel::clearFilters).padding(vertical = 8.dp, horizontal = 4.dp),
                )
            }

            Row(Modifier.padding(top = 18.dp), verticalAlignment = Alignment.Bottom) {
                SectionLabel(stringResource(Res.string.filter_how_plant_based), Modifier.weight(1f))
                Text(stopDescription(filters.strictness), style = bodyStyle(15.sp, FontWeight.SemiBold))
            }
            AndroidStrictnessSlider(filters.strictness, onSelect = viewModel::setStrictness, modifier = Modifier.padding(top = 10.dp))

            SectionLabel(stringResource(Res.string.filter_kind_of_place), Modifier.padding(top = 24.dp, bottom = 10.dp))
            FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                PlaceCategory.entries.forEach { category ->
                    val active = category in filters.categories
                    FilterChipBox(active = active, onClick = { viewModel.toggleCategory(category) }, inactiveBackground = Color.Transparent, elevated = false) {
                        VIcon(if (active) Res.drawable.ic_check else category.icon, size = 18.dp)
                        Text(stringResource(category.label), style = bodyStyle(14.sp, FontWeight.Medium))
                    }
                }
            }

            Row(
                Modifier
                    .padding(top = 22.dp)
                    .fillMaxWidth()
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = viewModel::toggleOpenNow),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(Modifier.weight(1f)) {
                    Text(stringResource(Res.string.filter_open_now), style = bodyStyle(16.sp))
                    Text(stringResource(Res.string.filter_open_now_description), style = bodyStyle(14.sp, color = VentrovaColors.Secondary))
                }
                Switch(
                    checked = filters.openNow,
                    onCheckedChange = { viewModel.toggleOpenNow() },
                    thumbContent = if (filters.openNow) {
                        { Icon(painterResource(Res.drawable.ic_check), contentDescription = null, modifier = Modifier.size(SwitchDefaults.IconSize)) }
                    } else {
                        null
                    },
                )
            }

            SectionLabel(stringResource(Res.string.filter_distance), Modifier.padding(top = 24.dp, bottom = 10.dp))
            SingleChoiceSegmentedButtonRow(Modifier.fillMaxWidth()) {
                RadiusOptions.forEachIndexed { index, radius ->
                    SegmentedButton(
                        selected = filters.radiusKm == radius,
                        onClick = { viewModel.setRadius(radius) },
                        shape = SegmentedButtonDefaults.itemShape(index, RadiusOptions.size),
                        colors = SegmentedButtonDefaults.colors(activeContainerColor = VentrovaColors.NavIndicator, activeContentColor = VentrovaColors.Ink),
                    ) {
                        Text(radiusLabel(radius), style = bodyStyle(14.sp, FontWeight.Medium))
                    }
                }
            }

            Surface(
                onClick = onDismiss,
                shape = RoundedCornerShape(28.dp),
                color = VentrovaColors.Accent,
                contentColor = Color.White,
                modifier = Modifier.padding(top = 26.dp).fillMaxWidth().height(56.dp),
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Text(pluralStringResource(Res.plurals.filter_show_places, state.nearby.size, state.nearby.size), style = bodyStyle(16.sp, FontWeight.SemiBold, Color.White))
                }
            }
        }
    }
}

@Composable
private fun SectionLabel(text: String, modifier: Modifier = Modifier) {
    Text(text, style = bodyStyle(14.sp, FontWeight.SemiBold, VentrovaColors.AccentDark), modifier = modifier)
}

/** The Android variant of the "How plant-based?" slider: a thick tonal track with a stop indicator. */
@Composable
private fun AndroidStrictnessSlider(selected: StrictnessStop?, onSelect: (StrictnessStop) -> Unit, modifier: Modifier = Modifier) {
    val stops = StrictnessStop.entries
    val index = selected?.ordinal ?: 0
    val fraction by animateFloatAsState(index / (stops.size - 1f))
    Column(modifier) {
        BoxWithConstraints(Modifier.fillMaxWidth().height(44.dp)) {
            val inset = 22.dp
            Canvas(Modifier.fillMaxWidth().height(44.dp)) {
                val left = inset.toPx()
                val right = size.width - inset.toPx()
                val trackTop = 14.dp.toPx()
                val trackHeight = 16.dp.toPx()
                val radius = CornerRadius(trackHeight / 2)
                drawRoundRect(VentrovaColors.AccentContainerSoft, Offset(left, trackTop), Size(right - left, trackHeight), radius)
                val x = left + (right - left) * fraction
                drawRoundRect(VentrovaColors.Accent, Offset(left - 6.dp.toPx(), trackTop), Size(x - left + 12.dp.toPx(), trackHeight), radius)
                drawRoundRect(VentrovaColors.Accent, Offset(x - 2.dp.toPx(), 0f), Size(4.dp.toPx(), size.height), CornerRadius(2.dp.toPx()))
            }
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                stops.forEach { stop ->
                    Box(
                        Modifier.size(44.dp).clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onSelect(stop) },
                        contentAlignment = Alignment.Center,
                    ) {
                        LevelGlyph(stop.glyphLevel, size = 12.dp, haloWidth = 2.dp)
                    }
                }
            }
        }
        Row(Modifier.fillMaxWidth().padding(top = 6.dp), horizontalArrangement = Arrangement.SpaceBetween) {
            stops.forEach { stop ->
                Text(
                    stopLabel(stop),
                    style = bodyStyle(12.5.sp, if (stop == selected) FontWeight.SemiBold else FontWeight.Medium, if (stop == selected) VentrovaColors.Ink else VentrovaColors.Subtle),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.width(76.dp).clickable { onSelect(stop) },
                )
            }
        }
    }
}
